import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { AdminTimeRangePicker } from '@/components/AdminTimeRangePicker';
import { CountryStateFilter } from '@/components/CountryStateFilter';
import { engagementCountryLabel, engagementLocationCodes } from '../lib/engagementColors';
import { HoverHint } from './EngagementNotReady';

/** Figma chart-header location control (page-level Event Location). */
export function EngagementChartCountryFilter({
  countryCode,
  onCountryCodeChange,
  availableCountryCodes = [],
  ariaLabel,
  className,
}: {
  countryCode: string;
  onCountryCodeChange: (countryCode: string) => void;
  availableCountryCodes?: readonly string[];
  ariaLabel: string;
  className?: string;
}) {
  return (
    <CountryStateFilter
      value={countryCode}
      onChange={onCountryCodeChange}
      availableCountryCodes={engagementLocationCodes(availableCountryCodes)}
      countryLabel={engagementCountryLabel}
      allCountriesLabel="All event locations"
      ariaLabel={ariaLabel}
      className={className ?? 'w-full sm:w-[280px]'}
    />
  );
}

/** Section-title “i” with hover copy for location semantics (TM-3661). */
export function SectionLocationHint({ text, label }: { text: string; label: string }) {
  return (
    <HoverHint text={text} label={label} className="cursor-help">
      <Info className="size-4 shrink-0 text-muted-foreground" aria-hidden />
    </HoverHint>
  );
}

/**
 * Card title plus page-level date and location pickers. Changing either updates
 * the whole Engagement page.
 */
export function EngagementChartHeader({
  title,
  subtitle,
  infoHint,
  range,
  onRangeChange,
  startDate,
  endDate,
  countryCode,
  onCountryCodeChange,
  availableCountryCodes,
  trailing,
}: {
  title: string;
  subtitle?: string;
  /** Business-friendly location-source help shown on the title “i”. */
  infoHint?: string;
  range?: string;
  onRangeChange?: (range: string) => void;
  startDate?: string;
  endDate?: string;
  countryCode: string;
  onCountryCodeChange: (countryCode: string) => void;
  availableCountryCodes?: readonly string[];
  trailing?: ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold leading-6">{title}</h3>
            {infoHint ? (
              <SectionLocationHint
                text={infoHint}
                label={`How ${title} uses the location filter. ${infoHint}`}
              />
            ) : null}
          </div>
          {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
        </div>
        {trailing}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {range != null && onRangeChange ? (
          <AdminTimeRangePicker
            preset="admin"
            value={range}
            onChange={onRangeChange}
            {...(startDate ? { startDate } : {})}
            {...(endDate ? { endDate } : {})}
            ariaLabel={`Change ${title} date range`}
            className="w-full min-w-[180px] sm:w-[220px]"
          />
        ) : null}
        <EngagementChartCountryFilter
          countryCode={countryCode}
          onCountryCodeChange={onCountryCodeChange}
          {...(availableCountryCodes ? { availableCountryCodes } : {})}
          ariaLabel={`Change ${title} event location`}
        />
      </div>
    </div>
  );
}
